// What colour a person is on the board.
//
// THE PROBLEM THIS EXISTS FOR
//
// The legend says a hero's colour identifies them. It did not: every member
// of the team had an empty chip_color, so `heroColorById` was empty, and
// every hero fell through to one shared fallback. Twenty-six people wearing
// the same colour is not an identity channel, and the fallback blue - a
// slightly violet blue, rendered at metalness 0.5 against a gold-horizon
// environment - is easily mistaken for the AUTOMATION purple, which is what
// the operator reported: "Mayuko turned purple on showing activity?"
//
// So a colour is DERIVED per person when the database has none, from a
// curated list rather than a hue wheel. A wheel would eventually hand
// somebody the goal gold or the unplanned orange, and those two mean
// something specific about the WORK; a person's colour must never be
// mistakable for a property of the tickets they hold.

import { stableHash } from '../boss/sprites';

/**
 * Deliberately away from the palette that means something:
 *   #c9a14a gold      a goal
 *   #e8721a orange    unplanned work
 *   #d8455a crimson   a boss minion
 *   #4a5a3a olive     a grave
 *   #a06bff violet    automation
 *   #eef2f7 silver    a sword or shield
 * so these are blues, teals, greens and pinks that cannot be confused with
 * any of them.
 */
export const HERO_PALETTE = [
  '#4fb0ff', '#3ecf8e', '#f2789f', '#7ad1e8', '#8fd14f',
  '#5f7cff', '#2fbfa0', '#e0719b', '#9ecbff', '#48b48a',
] as const;

/** The fallback used when nothing else is known. Kept exported so the
 *  legend and the board cannot disagree about it. */
export const HERO_FALLBACK = HERO_PALETTE[0];

/**
 * A person's colour. An explicit chip colour WINS - an operator who sets one
 * meant it - and otherwise a stable one is derived from the handle, so the
 * same person is the same colour on every surface and every reload.
 *
 * Keyed on the HANDLE rather than the id: a handle is what a person is called
 * and what survives a re-seed, and two people are never assigned the same
 * one.
 */
export function heroColourFor(handle: string, chipColour?: string | null): string {
  if (chipColour) return chipColour;
  const h = stableHash(handle.toLowerCase());
  return HERO_PALETTE[h % HERO_PALETTE.length]!;
}
